import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react'
import axios from 'axios'

// TimeSeriesExplorer — a dense, financial-style chart engine for the Marketing Brain.
// Reads "like stocks / candlesticks", not a thin cartoon line. Renders one metric as
// Candles / Area / Bars on a DPR-scaled canvas with a right-hand Y axis, faint
// gridlines, X-axis date labels, and a hover crosshair + tooltip. Interval (D/W/M),
// date-range navigation (◀ / ▶ + presets), and DIMENSION tabs (Time · Channel · Device
// · Region · Product) drive the two endpoints:
//   GET /api/v1/marketing-intel/timeseries  → { interval, points:[{t,value,open,high,low,close}] }
//   GET /api/v1/marketing-intel/breakdown   → { dimension, rows:[{key,value}] }
// When a query returns nothing we render a clearly-labelled representative sample so the
// engine never shows an empty box. Self-contained: scoped .tse-* CSS, light+dark tokens,
// no chart libraries (CSP-safe inline SVG / canvas only).
//
// Props: { metric, title, color }.
const API = '/api/v1/marketing-intel'

// ── date helpers (local calendar, 'YYYY-MM-DD') ──────────────────────────────
const pad2 = (n) => String(n).padStart(2, '0')
const toISO = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`
const parseISO = (s) => { const [y, m, d] = String(s).split('-').map(Number); return new Date(y, (m || 1) - 1, d || 1) }
const addDays = (s, n) => { const d = parseISO(s); d.setDate(d.getDate() + n); return toISO(d) }
const todayISO = () => toISO(new Date())
const dayDiff = (a, b) => Math.round((parseISO(b) - parseISO(a)) / 86400000)
const shortDate = (s) => parseISO(s).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })

// ── metric formatting ────────────────────────────────────────────────────────
const MONEY = new Set(['spend', 'revenue', 'pipeline'])
const isMoney = (m) => MONEY.has(String(m))
function fmtCompact(v, money) {
  const n = Number(v) || 0
  const a = Math.abs(n)
  let s
  if (a >= 1e9) s = (n / 1e9).toFixed(1) + 'B'
  else if (a >= 1e6) s = (n / 1e6).toFixed(1) + 'M'
  else if (a >= 1e3) s = (n / 1e3).toFixed(1) + 'k'
  else s = String(Math.round(n * 100) / 100)
  s = s.replace(/\.0([BMk])$/, '$1')
  return money ? '$' + s : s
}

// ── interval + dimension config ──────────────────────────────────────────────
const INTERVALS = [['day', 'D'], ['week', 'W'], ['month', 'M']]
const STEP = { day: 1, week: 7, month: 30 }
const DIMS = [
  ['time', 'Time'], ['channel', 'Channel'], ['device', 'Device'],
  ['region', 'Region'], ['product', 'Product'],
]
const TYPES = [['candles', 'Candles'], ['area', 'Area'], ['bars', 'Bars']]
const PRESETS = [['30d', 30], ['90d', 90], ['6m', 182], ['1y', 365]]
const BREAKDOWN_PALETTE = ['#4E9AC4', '#E24A3B', '#4FAE5A', '#8E7CC3', '#EAB43C', '#0D9488', '#C64B8B', '#5B6BE8', '#C77D19', '#2E9E5B', '#7C6AE0', '#3fb9c0']

// Bucket a range into representative date stamps (sample generator only).
function bucketDates(range, interval) {
  const step = STEP[interval] || 1
  const out = []
  let cur = range.from
  let guard = 0
  while (cur <= range.to && guard < 400) { out.push(cur); cur = addDays(cur, step); guard++ }
  return out.length ? out : [range.to]
}

// Deterministic-ish representative OHLC series for an empty/errored timeseries.
function sampleSeries(range, interval, metric) {
  const dates = bucketDates(range, interval)
  const money = isMoney(metric)
  const base = money ? 4200 : 420          // a sane per-bucket level (NOT compounded)
  const n = Math.max(1, dates.length - 1)
  const noise = base * 0.14
  let prev = base
  return dates.map((t, i) => {
    // Gentle bounded upward trend + a wave — never compounds, so values stay realistic.
    const trend = base * (1 + (i / n) * 0.4)
    const close = Math.max(1, trend + Math.sin(i * 0.55) * noise)
    const open = i === 0 ? base : prev
    const high = Math.max(open, close) + Math.abs(Math.cos(i * 0.9)) * noise * 0.6
    const low = Math.max(0, Math.min(open, close) - Math.abs(Math.sin(i * 1.1)) * noise * 0.5)
    prev = close
    const r2 = (x) => Math.round(x * 100) / 100
    return { t, open: r2(open), high: r2(high), low: r2(low), close: r2(close), value: r2(close) }
  })
}

// Representative breakdown rows for an empty/errored breakdown.
function sampleBreakdown(dimension, metric) {
  const sets = {
    channel: ['Google Ads', 'Meta Ads', 'Organic', 'Email', 'LinkedIn'],
    device: ['Mobile', 'Desktop', 'Tablet'],
    region: ['North America', 'Europe', 'APAC', 'LATAM'],
    product: ['Performance Polo', 'Tour Shorts', 'Quarter Zip', 'Cap'],
  }
  const labels = sets[dimension] || sets.channel
  const base = isMoney(metric) ? 18400 : 940
  return labels.map((k, i) => ({ key: k, value: Math.round(base * (1 - i * 0.17)) }))
}

// Read a resolved CSS custom property off an element (canvas inherits them), with fallback.
function cssVar(el, name, fb) {
  if (!el) return fb
  const v = getComputedStyle(el).getPropertyValue(name)
  return (v && v.trim()) || fb
}

export default function TimeSeriesExplorer({ metric = 'revenue', title, color = '#4E9AC4' }) {
  const money = isMoney(metric)
  const [dimension, setDimension] = useState('time')
  const [chartType, setChartType] = useState('candles')
  const [interval, setInterval] = useState('day')
  const [range, setRange] = useState(() => ({ from: addDays(todayISO(), -89), to: todayISO() }))
  const [points, setPoints] = useState([])
  const [rows, setRows] = useState([])
  const [sample, setSample] = useState(false)
  const [loading, setLoading] = useState(false)
  const [hover, setHover] = useState(null)

  const canvasRef = useRef(null)
  const geomRef = useRef({ padL: 8, bw: 1, n: 0 })

  // Load Nunito once (shared identity with the rest of the Brain surfaces).
  useEffect(() => {
    if (typeof document === 'undefined') return
    if (document.head.querySelector('link[data-tse-nunito]')) return
    const link = document.createElement('link')
    link.rel = 'stylesheet'
    link.href = 'https://fonts.googleapis.com/css2?family=Nunito:wght@400;500;600;700;800;900&display=swap'
    link.setAttribute('data-tse-nunito', '1')
    document.head.appendChild(link)
  }, [])

  // ── fetch (timeseries for Time, breakdown otherwise) ───────────────────────
  useEffect(() => {
    let alive = true
    setLoading(true)
    setHover(null)
    if (dimension === 'time') {
      axios.get(`${API}/timeseries`, { params: { metric, interval, from: range.from, to: range.to } })
        .then((r) => {
          if (!alive) return
          const raw = Array.isArray(r.data?.points) ? r.data.points : []
          // All-zero (or empty) real data reads as a dead flat line — fall back to a
          // clearly-labelled representative series so the chart always demonstrates itself.
          const pts = raw.filter((p) => (Number(p.value) || Number(p.close) || 0) !== 0)
          if (pts.length >= 2) { setPoints(raw); setSample(false) }
          else { setPoints(sampleSeries(range, interval, metric)); setSample(true) }
        })
        .catch(() => { if (alive) { setPoints(sampleSeries(range, interval, metric)); setSample(true) } })
        .finally(() => { if (alive) setLoading(false) })
    } else {
      axios.get(`${API}/breakdown`, { params: { metric, dimension, from: range.from, to: range.to } })
        .then((r) => {
          if (!alive) return
          const rws = Array.isArray(r.data?.rows) ? r.data.rows.filter((x) => (Number(x.value) || 0) > 0) : []
          if (rws.length) { setRows(rws); setSample(false) }
          else { setRows(sampleBreakdown(dimension, metric)); setSample(true) }
        })
        .catch(() => { if (alive) { setRows(sampleBreakdown(dimension, metric)); setSample(true) } })
        .finally(() => { if (alive) setLoading(false) })
    }
    return () => { alive = false }
  }, [metric, dimension, interval, range.from, range.to])

  // ── canvas render ──────────────────────────────────────────────────────────
  const draw = useCallback(() => {
    const cv = canvasRef.current
    if (!cv || dimension !== 'time') return
    const pts = points
    const dpr = window.devicePixelRatio || 1
    const W = cv.clientWidth || 640
    const H = 300
    cv.width = W * dpr; cv.height = H * dpr
    const ctx = cv.getContext('2d')
    ctx.scale(dpr, dpr)
    ctx.clearRect(0, 0, W, H)

    const inkCol = cssVar(cv, '--tse-mut', '#7C8794')
    const gridCol = cssVar(cv, '--tse-grid', 'rgba(124,135,148,.16)')
    const crossCol = cssVar(cv, '--tse-cross', '#7C8794')
    const upCol = cssVar(cv, '--tse-up', '#2E9E5B')
    const downCol = cssVar(cv, '--tse-down', '#E24A3B')

    const padL = 10, padR = 56, padT = 14, padB = 26
    const plotW = Math.max(10, W - padL - padR)
    const plotH = Math.max(10, H - padT - padB)
    const n = pts.length

    if (!n) {
      ctx.fillStyle = inkCol; ctx.font = '600 13px Nunito, system-ui, sans-serif'
      ctx.textAlign = 'center'; ctx.fillText('No data for this range', W / 2, H / 2)
      return
    }

    const bw = plotW / n
    const cx = (i) => padL + bw * (i + 0.5)

    // Y domain: candles use high/low; area/bars are volume-style (zero-based).
    let mn, mx
    if (chartType === 'candles') {
      mn = Math.min(...pts.map((p) => Number(p.low)))
      mx = Math.max(...pts.map((p) => Number(p.high)))
      const padv = (mx - mn) * 0.08 || Math.abs(mx) * 0.1 || 1
      mn -= padv; mx += padv
    } else {
      mn = 0
      mx = Math.max(...pts.map((p) => Number(p.value ?? p.close))) || 1
      mx *= 1.08
    }
    if (mx === mn) mx = mn + 1
    const Y = (v) => padT + plotH * (1 - (v - mn) / (mx - mn))

    // gridlines + right-hand Y ticks
    ctx.textAlign = 'left'; ctx.textBaseline = 'middle'
    ctx.font = '600 10.5px Nunito, system-ui, sans-serif'
    for (let g = 0; g <= 4; g++) {
      const val = mn + (mx - mn) * (g / 4)
      const yy = Y(val)
      ctx.strokeStyle = gridCol; ctx.lineWidth = 1
      ctx.beginPath(); ctx.moveTo(padL, yy); ctx.lineTo(W - padR, yy); ctx.stroke()
      ctx.fillStyle = inkCol
      ctx.fillText(fmtCompact(val, money), W - padR + 6, yy)
    }

    // X date labels (strided ≈ 6)
    ctx.textAlign = 'center'; ctx.textBaseline = 'alphabetic'
    const stride = Math.max(1, Math.ceil(n / 6))
    for (let i = 0; i < n; i += stride) {
      ctx.fillStyle = inkCol
      ctx.fillText(shortDate(pts[i].t), cx(i), H - 8)
    }

    // series
    if (chartType === 'candles') {
      const bodyW = Math.max(1.5, bw * 0.6)
      for (let i = 0; i < n; i++) {
        const p = pts[i]
        const o = Number(p.open), c = Number(p.close), hi = Number(p.high), lo = Number(p.low)
        const up = c >= o
        const col = up ? upCol : downCol
        const xC = cx(i)
        ctx.strokeStyle = col; ctx.lineWidth = 1
        ctx.beginPath(); ctx.moveTo(xC, Y(hi)); ctx.lineTo(xC, Y(lo)); ctx.stroke()
        const top = Math.min(Y(o), Y(c))
        const bot = Math.max(Y(o), Y(c))
        ctx.fillStyle = col
        ctx.fillRect(xC - bodyW / 2, top, bodyW, Math.max(1, bot - top))
      }
    } else if (chartType === 'area') {
      const grad = ctx.createLinearGradient(0, padT, 0, padT + plotH)
      grad.addColorStop(0, hexA(color, 0.30)); grad.addColorStop(1, hexA(color, 0))
      ctx.beginPath()
      pts.forEach((p, i) => { const v = Number(p.value ?? p.close); i ? ctx.lineTo(cx(i), Y(v)) : ctx.moveTo(cx(i), Y(v)) })
      ctx.lineTo(cx(n - 1), Y(mn)); ctx.lineTo(cx(0), Y(mn)); ctx.closePath()
      ctx.fillStyle = grad; ctx.fill()
      ctx.beginPath()
      pts.forEach((p, i) => { const v = Number(p.value ?? p.close); i ? ctx.lineTo(cx(i), Y(v)) : ctx.moveTo(cx(i), Y(v)) })
      ctx.strokeStyle = color; ctx.lineWidth = 2.4; ctx.lineJoin = 'round'; ctx.lineCap = 'round'; ctx.stroke()
    } else { // bars (volume-style)
      const bodyW = Math.max(1.5, bw * 0.62)
      ctx.fillStyle = color
      for (let i = 0; i < n; i++) {
        const v = Number(pts[i].value ?? pts[i].close)
        const y = Y(v)
        ctx.fillRect(cx(i) - bodyW / 2, y, bodyW, Math.max(1, Y(mn) - y))
      }
    }

    // crosshair
    if (hover != null && hover >= 0 && hover < n) {
      const xC = cx(hover)
      const p = pts[hover]
      const v = chartType === 'candles' ? Number(p.close) : Number(p.value ?? p.close)
      const yv = Y(v)
      ctx.save()
      ctx.strokeStyle = crossCol; ctx.globalAlpha = 0.55; ctx.lineWidth = 1; ctx.setLineDash([3, 4])
      ctx.beginPath(); ctx.moveTo(xC, padT); ctx.lineTo(xC, padT + plotH); ctx.stroke()
      ctx.beginPath(); ctx.moveTo(padL, yv); ctx.lineTo(W - padR, yv); ctx.stroke()
      ctx.restore()
      ctx.fillStyle = color
      ctx.beginPath(); ctx.arc(xC, yv, 3.5, 0, Math.PI * 2); ctx.fill()
    }

    geomRef.current = { padL, bw, n, W }
  }, [points, chartType, dimension, hover, color, money])

  useEffect(() => {
    draw()
    window.addEventListener('resize', draw)
    return () => window.removeEventListener('resize', draw)
  }, [draw])

  const onMove = (e) => {
    const cv = canvasRef.current
    if (!cv) return
    const { padL, bw, n } = geomRef.current
    if (!n) return
    const rect = cv.getBoundingClientRect()
    const x = e.clientX - rect.left
    let i = Math.floor((x - padL) / bw)
    if (i < 0) i = 0; if (i > n - 1) i = n - 1
    setHover(i)
  }
  const onLeave = () => setHover(null)

  // ── date-range navigation ──────────────────────────────────────────────────
  const span = Math.max(1, dayDiff(range.from, range.to) + 1)
  const shift = (dir) => {
    if (dir < 0) {
      const to = addDays(range.from, -1)
      setRange({ from: addDays(to, -(span - 1)), to })
    } else {
      let from = addDays(range.to, 1)
      let to = addDays(from, span - 1)
      const t = todayISO()
      if (to > t) { to = t; from = addDays(to, -(span - 1)) }
      setRange({ from, to })
    }
  }
  const atToday = range.to >= todayISO()
  const applyPreset = (days) => setRange({ from: addDays(todayISO(), -(days - 1)), to: todayISO() })
  const activePreset = useMemo(() => (atToday ? (PRESETS.find(([, d]) => d === span) || [])[0] : null), [atToday, span])

  // hovered tooltip payload
  const hp = hover != null && points[hover] ? points[hover] : null

  const heading = title || `${metric.charAt(0).toUpperCase() + metric.slice(1)} explorer`

  return (
    <div className="tse-root" style={{ '--tse-accent': color }}>
      <style dangerouslySetInnerHTML={{ __html: CSS }} />

      {/* toolbar row 1 — dimension tabs + (time only) chart-type toggle */}
      <div className="tse-bar">
        <div className="tse-tabs" role="tablist" aria-label="Dimension">
          {DIMS.map(([id, lab]) => (
            <button key={id} role="tab" aria-selected={dimension === id}
              className={`tse-tab ${dimension === id ? 'on' : ''}`} onClick={() => setDimension(id)}>{lab}</button>
          ))}
        </div>
        {dimension === 'time' && (
          <div className="tse-seg" role="group" aria-label="Chart type">
            {TYPES.map(([id, lab]) => (
              <button key={id} className={`tse-segb ${chartType === id ? 'on' : ''}`} onClick={() => setChartType(id)}>{lab}</button>
            ))}
          </div>
        )}
      </div>

      {/* toolbar row 2 — (time) interval + date-range nav + presets */}
      <div className="tse-bar tse-bar2">
        <div className="tse-left">
          {dimension === 'time' && (
            <div className="tse-seg tse-seg-sm" role="group" aria-label="Interval">
              {INTERVALS.map(([id, lab]) => (
                <button key={id} className={`tse-segb ${interval === id ? 'on' : ''}`} title={id}
                  onClick={() => setInterval(id)}>{lab}</button>
              ))}
            </div>
          )}
          <div className="tse-nav">
            <button className="tse-navb" onClick={() => shift(-1)} aria-label="Previous period">◀</button>
            <span className="tse-range">{shortDate(range.from)} – {shortDate(range.to)}</span>
            <button className="tse-navb" onClick={() => shift(1)} disabled={atToday} aria-label="Next period">▶</button>
          </div>
        </div>
        <div className="tse-presets">
          {PRESETS.map(([id, days]) => (
            <button key={id} className={`tse-preset ${activePreset === id ? 'on' : ''}`} onClick={() => applyPreset(days)}>{id}</button>
          ))}
        </div>
      </div>

      {/* body */}
      {dimension === 'time' ? (
        <div className="tse-chart">
          {sample && <span className="tse-sample"><i /> sample</span>}
          <canvas ref={canvasRef} height="300" style={{ width: '100%', display: 'block' }}
            onMouseMove={onMove} onMouseLeave={onLeave} />
          {hp && (
            <div className="tse-tip" style={tipPos(geomRef.current, hover)}>
              <div className="tse-tip-d">{shortDate(hp.t)}</div>
              {chartType === 'candles' ? (
                <div className="tse-tip-ohlc">
                  <span><b>O</b> {fmtCompact(hp.open, money)}</span>
                  <span><b>H</b> {fmtCompact(hp.high, money)}</span>
                  <span><b>L</b> {fmtCompact(hp.low, money)}</span>
                  <span><b>C</b> {fmtCompact(hp.close, money)}</span>
                </div>
              ) : (
                <div className="tse-tip-v">{fmtCompact(hp.value ?? hp.close, money)}</div>
              )}
            </div>
          )}
        </div>
      ) : (
        <BreakdownBars rows={rows} money={money} title={heading} dimension={dimension} />
      )}
    </div>
  )
}

// Horizontal breakdown for Channel / Device / Region / Product.
function BreakdownBars({ rows, money }) {
  const mx = Math.max(...rows.map((r) => Number(r.value) || 0), 0.0001)
  return (
    <div className="tse-bd">
      {rows.map((r, i) => {
        const v = Number(r.value) || 0
        const c = BREAKDOWN_PALETTE[i % BREAKDOWN_PALETTE.length]
        return (
          <div key={i} className="tse-bd-row">
            <div className="tse-bd-l" title={r.key}>{r.key}</div>
            <div className="tse-bd-track">
              <div className="tse-bd-fill" style={{ width: `${Math.max(2, (v / mx) * 100)}%`, background: c, animationDelay: `${i * 0.05}s` }} />
            </div>
            <div className="tse-bd-v">{fmtCompact(v, money)}</div>
          </div>
        )
      })}
    </div>
  )
}

// Position the tooltip near the hovered candle, clamped inside the plot.
function tipPos(geom, hover) {
  const { padL, bw, W } = geom
  if (hover == null || !bw) return { display: 'none' }
  const x = padL + bw * (hover + 0.5)
  const left = Math.max(8, Math.min((W || 640) - 150, x - 70))
  return { left: `${left}px`, top: '8px' }
}

// #rrggbb → rgba() at alpha a. Falls back to the input for non-hex colors.
function hexA(hex, a) {
  const m = /^#?([0-9a-f]{6})$/i.exec(String(hex).trim())
  if (!m) return hex
  const int = parseInt(m[1], 16)
  return `rgba(${(int >> 16) & 255},${(int >> 8) & 255},${int & 255},${a})`
}

const CSS = `
.tse-root{
  --tse-panel:#FFFFFF; --tse-panel2:#F6F6F0; --tse-line:#ECEBE3; --tse-ink:#2E3A45; --tse-mut:#7C8794; --tse-dim:#AEB6BD;
  --tse-grid:rgba(124,135,148,.16); --tse-cross:#7C8794; --tse-up:#2E9E5B; --tse-down:#E24A3B;
  font-family:Nunito,system-ui,-apple-system,sans-serif;color:var(--tse-ink);-webkit-font-smoothing:antialiased;
}
[data-theme="dark"] .tse-root{
  --tse-panel:#171B22; --tse-panel2:#12161C; --tse-line:#262C36; --tse-ink:#E7ECF1; --tse-mut:#95A0AD; --tse-dim:#5C6672;
  --tse-grid:rgba(149,160,173,.16); --tse-cross:#95A0AD; --tse-up:#5CC47C; --tse-down:#F0575C;
}
@media(prefers-color-scheme:dark){:root:not([data-theme="light"]) .tse-root{
  --tse-panel:#171B22; --tse-panel2:#12161C; --tse-line:#262C36; --tse-ink:#E7ECF1; --tse-mut:#95A0AD; --tse-dim:#5C6672;
  --tse-grid:rgba(149,160,173,.16); --tse-cross:#95A0AD; --tse-up:#5CC47C; --tse-down:#F0575C;
}}
.tse-root *{box-sizing:border-box}
.tse-bar{display:flex;align-items:center;justify-content:space-between;gap:10px;flex-wrap:wrap;}
.tse-bar2{margin-top:9px;}
.tse-left{display:flex;align-items:center;gap:10px;flex-wrap:wrap;}
/* dimension tabs */
.tse-tabs{display:flex;gap:4px;background:var(--tse-panel2);border:1px solid var(--tse-line);border-radius:11px;padding:3px;}
.tse-tab{border:none;background:none;font:800 12px Nunito,sans-serif;color:var(--tse-mut);padding:6px 12px;border-radius:8px;cursor:pointer;transition:.14s;}
.tse-tab:hover{color:var(--tse-ink);}
.tse-tab.on{background:var(--tse-panel);color:var(--tse-accent);box-shadow:0 1px 2px rgba(46,58,69,.10);}
[data-theme="dark"] .tse-tab.on{box-shadow:0 1px 2px rgba(0,0,0,.4);}
/* segmented toggles */
.tse-seg{display:inline-flex;border:1px solid var(--tse-line);border-radius:10px;overflow:hidden;}
.tse-segb{border:none;background:var(--tse-panel);font:800 12px Nunito,sans-serif;color:var(--tse-mut);padding:6px 13px;cursor:pointer;transition:.14s;border-left:1px solid var(--tse-line);}
.tse-segb:first-child{border-left:none;}
.tse-segb:hover{color:var(--tse-ink);}
.tse-segb.on{background:var(--tse-accent);color:#fff;}
.tse-seg-sm .tse-segb{padding:6px 11px;min-width:30px;}
/* date nav */
.tse-nav{display:inline-flex;align-items:center;gap:4px;}
.tse-navb{border:1px solid var(--tse-line);background:var(--tse-panel);color:var(--tse-mut);border-radius:8px;width:28px;height:28px;font-size:11px;cursor:pointer;transition:.14s;display:inline-flex;align-items:center;justify-content:center;}
.tse-navb:hover:not(:disabled){color:var(--tse-ink);border-color:var(--tse-accent);}
.tse-navb:disabled{opacity:.4;cursor:default;}
.tse-range{font:800 12.5px Nunito,sans-serif;color:var(--tse-ink);min-width:118px;text-align:center;font-variant-numeric:tabular-nums;}
.tse-presets{display:inline-flex;gap:4px;}
.tse-preset{border:1px solid var(--tse-line);background:var(--tse-panel);color:var(--tse-mut);font:800 11.5px Nunito,sans-serif;border-radius:8px;padding:6px 10px;cursor:pointer;transition:.14s;}
.tse-preset:hover{color:var(--tse-ink);}
.tse-preset.on{background:var(--tse-accent);color:#fff;border-color:transparent;}
/* chart */
.tse-chart{position:relative;margin-top:12px;}
.tse-chart canvas{cursor:crosshair;}
.tse-sample{position:absolute;top:0;right:0;display:inline-flex;align-items:center;gap:6px;font:800 10.5px Nunito,sans-serif;color:#B4841B;background:rgba(234,180,60,.14);border:1px solid rgba(234,180,60,.35);border-radius:20px;padding:3px 9px;z-index:2;}
[data-theme="dark"] .tse-sample{color:#EAB43C;}
.tse-sample i{width:6px;height:6px;border-radius:50%;background:#EAB43C;}
.tse-tip{position:absolute;pointer-events:none;background:var(--tse-panel);border:1px solid var(--tse-line);border-radius:9px;padding:7px 10px;box-shadow:0 6px 18px rgba(46,58,69,.16);z-index:3;min-width:130px;}
[data-theme="dark"] .tse-tip{box-shadow:0 6px 18px rgba(0,0,0,.5);}
.tse-tip-d{font:800 11px Nunito,sans-serif;color:var(--tse-mut);margin-bottom:4px;}
.tse-tip-ohlc{display:grid;grid-template-columns:1fr 1fr;gap:2px 12px;font:700 12px Nunito,sans-serif;color:var(--tse-ink);font-variant-numeric:tabular-nums;}
.tse-tip-ohlc b{color:var(--tse-mut);font-weight:800;margin-right:3px;}
.tse-tip-v{font:800 15px Nunito,sans-serif;color:var(--tse-ink);font-variant-numeric:tabular-nums;}
/* breakdown */
.tse-bd{margin-top:14px;}
.tse-bd-row{display:flex;align-items:center;gap:11px;margin:9px 0;}
.tse-bd-l{width:130px;font:700 12.5px Nunito,sans-serif;color:var(--tse-mut);flex:none;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
.tse-bd-track{flex:1;height:26px;background:var(--tse-panel2);border-radius:8px;overflow:hidden;}
.tse-bd-fill{height:100%;border-radius:8px;transform-origin:left;animation:tse-grow .7s cubic-bezier(.3,1,.4,1) both;}
@keyframes tse-grow{from{transform:scaleX(0)}to{transform:scaleX(1)}}
.tse-bd-v{width:70px;text-align:right;font:800 13px Nunito,sans-serif;color:var(--tse-ink);font-variant-numeric:tabular-nums;flex:none;}
`
